import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import DocumentTemplateGeneralDataContainer from './generalData/DocumentTemplateGeneralDataContainer';
import DocumentTemplateFiltersContainer from './documentTemplateFilters/DocumentTemplateFiltersContainer';
import UserGroupListContainer from '../../../common/components/userGroup/userGroupList/UserGroupListContainer';
import { TEMPLATE_QUERY } from '../queries';
import { DOCUMENT_TEMPLATE_USER_GROUPS_UPDATED } from '../subscriptions';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { SAVE_TEMPLATE_USER_GROUPS } from '../mutations';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import DocumentTemplateSignatureContainer from './signature/DocumentTemplateSignatureContainer';
import useLanguageTracker from 'hooks/useLanguageTracker';
import { FakeText, Card } from '@totalsoft/rocket-ui';

const DocumentTemplateContainer = ({ documentTemplateId, onChangeDocumentTemplateTitle, onRedirect, bulkPrintingYN }) => {
  const [t] = useTranslation();
  const addToast = useToast();

  // #region Queries

  const { loading, data, refetch } = useQuery(TEMPLATE_QUERY, {
    variables: {
      documentTemplateId: documentTemplateId
    },
    skip: documentTemplateId === undefined
  });

  // #endregion

  // #region Mutations

  const [saveDocumentTemplateXUserGroups] = useMutation(SAVE_TEMPLATE_USER_GROUPS);

  // #endregion

  // #region Subscriptions

  useSubscription(DOCUMENT_TEMPLATE_USER_GROUPS_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('DocumentTemplate.UserGroupsSavingSucceeded'), 'success');
      if (data.documentTemplateXUserGroupsSaved.documentTemplateXUserGroups.length > 0)
        client.cache.evict({
          id: `DocumentTemplateItem:{"documentTemplateId":${data.documentTemplateXUserGroupsSaved.documentTemplateXUserGroups[0].documentTemplateId}}`
        });
    }
  });

  // #endregion

  // #region Handlers

  const handleSaveDocumentTemplateXUserGroups = useCallback(
    selectedUserGroups => {
      const templateXUserGroups = selectedUserGroups.map(item => {
        return {
          documentTemplateId: documentTemplateId,
          userGroupId: item.userGroupId
        };
      });
      saveDocumentTemplateXUserGroups({
        variables: {
          input: { documentTemplateXUserGroups: templateXUserGroups, documentTemplateId: documentTemplateId }
        }
      });
    },
    [saveDocumentTemplateXUserGroups, documentTemplateId]
  );

  // #endregion

  // #region Side effects

  const onLanguageChange = useCallback(() => {
    refetch();
  }, [refetch]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  // #endregion

  return loading ? (
    <Card>
      <FakeText lines={15} />
    </Card>
  ) : (
    <>
      <DocumentTemplateGeneralDataContainer
        data={data?.template}
        filters={data && data.template ? data.template.documentTemplateFilters : []}
        onChangeDocumentTemplateTitle={onChangeDocumentTemplateTitle}
        onRedirect={onRedirect}
        bulkPrintingYN={bulkPrintingYN}
        loading={loading}
      />

      {documentTemplateId !== undefined && documentTemplateId !== null && (
        <>
          {data?.template?.bulkPrintingYN === false && (
            <DocumentTemplateFiltersContainer
              condition={data && data.template ? data.template.filterCondition : ''}
              filters={data && data.template ? data.template.documentTemplateFilters : []}
              documentTemplateId={data?.template?.documentTemplateId}
            />
          )}
          <UserGroupListContainer
            xUserGroups={data && data.template ? data.template.documentTemplateXUserGroups : []}
            onXUserGroupsSave={handleSaveDocumentTemplateXUserGroups}
            parentLoaded={!loading}
          />

          {data?.template?.bulkPrintingYN === false && <DocumentTemplateSignatureContainer data={data?.template} />}
        </>
      )}
    </>
  );
};

DocumentTemplateContainer.propTypes = {
  documentTemplateId: PropTypes.number,
  onChangeDocumentTemplateTitle: PropTypes.func,
  onRedirect: PropTypes.func,
  bulkPrintingYN: PropTypes.bool
};

export default DocumentTemplateContainer;
